import { filterOnlyHavingParameterSet } from '../filters/byParameterName.js';
import StringifiedParameterFormatter from '../formatters/StringifiedParameterFormatter.js';
import buildFiltersForHittingChart from '../filters/hittingChartsFilters.js';
import { PitchData, ResultData, PitchReprocessed, HitData } from '../enums/index.js';
import UniqueParametersForPitchAndHitProvider from './UniqueParametersForPitchAndHitProvider.js';
import { ChartSeries, Series, BaseballResultsProcessor } from '../entities/index.js';
import { ResultPair } from '../valueObjects/index.js';

const buildFiltersArray = function({
    // tagging context
    batterId, pitchResults, pitchTypes, zones, pitcherIds, pitcherHandedness,
    // scoring context
    outs, balls, strikes, runnersOn,
}) {
    let battingChartFilters = buildFiltersForHittingChart({
        batterId, pitchResults, pitchTypes, zones, pitcherIds, pitcherHandedness,
        outs, balls, strikes, runnersOn
    });

    let filterOnlyWithParameter = filterOnlyHavingParameterSet([ResultPair.getPitchProperty()].concat(ResultData.getDataPath(PitchData.StrikeZoneFront.key)));

    return battingChartFilters.concat(
        filterOnlyWithParameter,
    );
};

const getSeriesForStrikeZoneChart = function(
    results, customResultsProcessor,
    {
        // tagging context
        batterId, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    } = {}
) {
    let chartSeries = [];
    let resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
    let chartFilters = buildFiltersArray({
        batterId, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        outs, balls, strikes, runnersOn,
    });

    // match pitch and hit...
    let matchedResults = BaseballResultsProcessor.matchPitchesAndHits(results);

    let pairsWithLocation =  resultsProcessor.filterData(matchedResults, chartFilters);
    let pairsPerPitchResult = resultsProcessor.groupData(pairsWithLocation, [[ResultPair.getPitchProperty()].concat(ResultData.getDataPath(PitchData.Result.key))]);
    for (let pitchResult in pairsPerPitchResult) {
        let resultsMapped = resultsProcessor.mapCollectionProperties(pairsPerPitchResult[pitchResult], [
            [ResultPair.getPitchProperty(), ResultData.Id.key],
            [ResultPair.getPitchProperty(), ResultData.HitId.key],
            [ResultPair.getPitchProperty()].concat(ResultData.getDataPath(PitchData.StrikeZoneFront.key))
        ]);

        const dataForPitchResult = new Series(pitchResult, 2);

        for (let i = 0; i < resultsMapped.length; i++) {
            let formatted = StringifiedParameterFormatter(
                resultsMapped[i][PitchData.StrikeZoneFront.key], ';', PitchData.StrikeZoneFront.stringifiedAs, parseFloat
            );

            // TODO - if we need to mirror it -  x: formatted.X * -1,
            dataForPitchResult.add(resultsMapped[i][ResultData.Id.key], formatted.X, formatted.Z);
        }

        chartSeries.push(dataForPitchResult);
    }

    return new ChartSeries(chartSeries, Object.keys(pairsPerPitchResult));
};

const listUniqueParametersForStrikeZoneChart = function(
    results, customResultsProcessor,
    {
        // tagging context
        batterId, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    } = {},
    pitchParameters, hitParameters, pitchToHitParamsToMerge, combinedPitchResult
) {
    pitchParameters = pitchParameters || [
        // tagging context
        [ResultData.BatterTagId.key],
        [ResultData.TagId.key],
        ResultData.getDataPath(PitchData.SwingHandedness.key),
        ResultData.getDataPath(PitchData.Handedness.key),
        ResultData.getDataPath(PitchData.Result.key),
        ResultData.getDataPath(PitchData.Classification.key),
        ResultData.getDataPath(PitchReprocessed.StrikeZonePositionR.key),
        // scoring context
        ResultData.getDataPath(PitchData.BeforePlayOuts.key),
        ResultData.getDataPath(PitchData.BeforePlayBalls.key),
        ResultData.getDataPath(PitchData.BeforePlayStrikes.key),
        ResultData.getDataPath(PitchData.BeforePlayRunnersOn.key),
    ];

    hitParameters = hitParameters || [
        [ResultData.TagId.key],
        ResultData.getDataPath(HitData.SwingHandedness.key),
    ];

    pitchToHitParamsToMerge = pitchToHitParamsToMerge || [
        0, undefined, 1
    ];

    const resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
    let chartFilters = buildFiltersArray({
        batterId, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        outs, balls, strikes, runnersOn,
    });

    // match pitch and hit...
    let matchedResults = BaseballResultsProcessor.matchPitchesAndHits(results);

    let pairsWithLocation = resultsProcessor.filterData(matchedResults, chartFilters);
    
    return UniqueParametersForPitchAndHitProvider(
        pitchParameters.map(x => [ResultPair.getPitchProperty()].concat(x)),
        hitParameters.map(x => [ResultPair.getHitProperty()].concat(x)),
        pitchToHitParamsToMerge,
        pairsWithLocation, // it is ok, hit and pitch filtering is included on parameters level
        pairsWithLocation,
        resultsProcessor,
        combinedPitchResult
    );
};

export default getSeriesForStrikeZoneChart;

export {
    getSeriesForStrikeZoneChart,
    listUniqueParametersForStrikeZoneChart,
    buildFiltersArray
};